import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Plane, 
  Search, 
  Clock, 
  MapPin, 
  AlertCircle, 
  CheckCircle2,
  RefreshCw
} from 'lucide-react';

const FlightAPITester = () => {
  const { t } = useLanguage();
  const [flightNumber, setFlightNumber] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [flightData, setFlightData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const searchFlight = async () => {
    if (!flightNumber.trim()) {
      setError(t('enterFlightNumber'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      setFlightData(null);

      const token = localStorage.getItem('token');
      let url = `${backendUrl}/api/flights/status/${encodeURIComponent(flightNumber)}`;
      
      if (searchDate) {
        url += `?date=${searchDate}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFlightData(data);
      } else if (response.status === 404) {
        setError(t('flightNotFound').replace('{flight}', flightNumber));
      } else if (response.status === 503) {
        setError(t('flightApiUnavailable'));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t('errorSearching'));
      }
    } catch (err) {
      setError(t('flightApiConnectionError'));
      console.error('Error searching flight:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in_transit': return 'bg-yellow-100 text-yellow-800';
      case 'arrived': return 'bg-green-100 text-green-800';
      case 'delayed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('notDefined');
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5" />
            {t('checkFlightStatus')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('flightNumber')} *</Label>
                <Input
                  value={flightNumber}
                  onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
                  placeholder="AF001, BA123, etc."
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('dateOptional')}</Label>
                <Input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                />
              </div>
              
              <div className="flex items-end">
                <Button 
                  onClick={searchFlight}
                  disabled={loading || !flightNumber.trim()}
                  className="w-full"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {t('search')}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {flightData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Plane className="h-5 w-5" />
                {t('flight')} {flightData.flight_number}
              </span>
              <Badge className={getStatusBadgeColor(flightData.status)}>
                {t(flightData.status)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Informations générales */}
              <div className="space-y-4">
                <h4 className="font-semibold">{t('generalInfo')}</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('airline')} :</span>
                    <span>{flightData.airline_name || t('notSpecified')}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('aircraftType')} :</span>
                    <span>{flightData.aircraft_type || t('notSpecified')}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('route')} :</span>
                    <span>
                      {flightData.departure_airport || '?'} → {flightData.arrival_airport || '?'}
                    </span>
                  </div>
                  
                  {flightData.delay_minutes !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('delayMinutes')} :</span>
                      <span className={flightData.delay_minutes > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                        {flightData.delay_minutes > 0 ? `+${flightData.delay_minutes}` : '0'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Horaires */}
              <div className="space-y-4">
                <h4 className="font-semibold">{t('schedules')}</h4>
                
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-600">{t('scheduledDeparture')}</div>
                    <div className="font-medium">
                      {formatDateTime(flightData.scheduled_departure)}
                    </div>
                  </div>
                  
                  {flightData.actual_departure && (
                    <div>
                      <div className="text-sm text-gray-600">{t('actualDeparture')}</div>
                      <div className="font-medium text-green-600">
                        {formatDateTime(flightData.actual_departure)}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <div className="text-sm text-gray-600">{t('scheduledArrival')}</div>
                    <div className="font-medium">
                      {formatDateTime(flightData.scheduled_arrival)}
                    </div>
                  </div>
                  
                  {flightData.estimated_arrival && (
                    <div>
                      <div className="text-sm text-gray-600">{t('estimatedArrival')}</div>
                      <div className="font-medium text-blue-600">
                        {formatDateTime(flightData.estimated_arrival)}
                      </div>
                    </div>
                  )}
                  
                  {flightData.actual_arrival && (
                    <div>
                      <div className="text-sm text-gray-600">{t('actualArrival')}</div>
                      <div className="font-medium text-green-600">
                        {formatDateTime(flightData.actual_arrival)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Informations aéroport */}
            {(flightData.gate || flightData.terminal) && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="font-semibold mb-2">{t('airportInfo')}</h4>
                <div className="flex gap-4">
                  {flightData.terminal && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">
                        {t('terminal')} {flightData.terminal}
                      </span>
                    </div>
                  )}
                  
                  {flightData.gate && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">
                        {t('gate')} {flightData.gate}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mise à jour */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>
                  {t('lastUpdated')} : {formatDateTime(flightData.updated_at)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FlightAPITester;